import { User, Users, X, ArrowLeft } from 'lucide-react';

interface ChooseSubjectCountModalProps {
    isOpen: boolean;
    onClose: () => void;
    onBack: () => void;
    onChoose: (mode: 'single' | 'bulk') => void;
    planName: string;
}

/**
 * One person, or several?
 *
 * Asked after the plan rather than before it, because a batch shares one plan —
 * choosing the plan first is what makes "everyone on this plan, one payment"
 * a coherent thing to offer.
 */
export default function ChooseSubjectCountModal({
    isOpen,
    onClose,
    onBack,
    onChoose,
    planName,
}: ChooseSubjectCountModalProps) {
    if (!isOpen) return null;

    const options = [
        {
            mode: 'single' as const,
            icon: User,
            title: 'Just one person',
            description: 'Enter their details, upload documents, and pay.',
        },
        {
            mode: 'bulk' as const,
            icon: Users,
            title: 'Several people',
            description: 'Enter everyone in one go and pay for them all in a single payment.',
        },
    ];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
                <button
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
                >
                    <X className="h-4 w-4 text-gray-500" />
                </button>

                <h2 className="text-lg font-bold text-gray-900">How many people?</h2>
                <p className="mt-1 text-sm text-gray-500">
                    You picked the <span className="font-medium text-gray-700">{planName}</span> plan.
                </p>

                <div className="mt-5 flex flex-col gap-3">
                    {options.map(({ mode, icon: Icon, title, description }) => (
                        <button
                            key={mode}
                            type="button"
                            onClick={() => onChoose(mode)}
                            className="flex items-start gap-3 rounded-xl border border-gray-200 p-4 text-left transition-colors hover:border-[#FF6B35] hover:bg-[#FFF3EE]"
                        >
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FFF3EE]">
                                <Icon className="h-5 w-5 text-[#FF6B35]" />
                            </span>
                            <span>
                                <span className="block text-sm font-semibold text-gray-900">{title}</span>
                                <span className="mt-0.5 block text-xs text-gray-500">{description}</span>
                            </span>
                        </button>
                    ))}
                </div>

                <button
                    type="button"
                    onClick={onBack}
                    className="mt-5 flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Change plan
                </button>
            </div>
        </div>
    );
}
